import {
  FloatingFocusManager,
  FloatingPortal,
  useMergeRefs,
  useTransitionStatus,
} from '@floating-ui/react';
import { useEffect, useState, type default as React } from 'react';
import { Close } from '#icons/Close';
import { Button } from '../../Input/Button';
import { IconButton } from '../../Input/IconButton';
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerInner,
  DrawerScrollableContent,
  DrawerSubtitle,
  DrawerTitle,
  DrawerTitleWrapper,
  OverlayWrapper,
} from './styles';
import { DrawerContext, useDrawer, useDrawerContext } from './DrawerContext';
import { type DrawerOptions } from './types';

// Keep these in sync with the CSS transition durations in `styles.tsx`.
const OPEN_TRANSITION_DURATION = 300;
const CLOSE_TRANSITION_DURATION = 240;

export function Drawer({
  children,
  onClose,
  onOpenChange,
  ...options
}: {
  children: React.ReactNode;
} & DrawerOptions) {
  const _onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose?.();
    }
  };

  const drawer = useDrawer({ ...options, onClose, onOpenChange: onOpenChange ?? _onOpenChange });

  return (
    <DrawerContext value={drawer}>
      <DrawerContent>{children}</DrawerContent>
    </DrawerContext>
  );
}

type DrawerContentProps = React.HTMLProps<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};

function DrawerContent({ ref: propRef, ...props }: DrawerContentProps) {
  const {
    context: floatingContext,
    getFloatingProps,
    primaryAction,
    secondaryAction,
    onClose,
    disableClose,
    closeOnClickOutside,
    width,
    title,
    subtitle,
    refs,
  } = useDrawerContext();
  const ref = useMergeRefs([
    (node) => {
      refs.setFloating(node);
    },
    propRef,
  ]);

  const { isMounted, status } = useTransitionStatus(floatingContext, {
    duration: {
      open: OPEN_TRANSITION_DURATION,
      close: CLOSE_TRANSITION_DURATION,
    },
  });

  // Lock background scroll as soon as the drawer mounts (before the slide-in transition starts),
  // and keep it locked until the close transition has fully finished. Locking/unlocking scroll
  // removes/restores the page's vertical scrollbar, which changes the viewport width. Since the
  // drawer is right-anchored (`position: fixed; right: 0`), that width change shifts its resting
  // position - so it must happen while the drawer is fully off-screen (before opening starts, or
  // after closing finishes), never mid-transition, otherwise the drawer visibly jumps/bounces.
  const [lockScroll, setLockScroll] = useState(false);

  useEffect(() => {
    if (status === 'initial' || status === 'open') {
      setLockScroll(true);

      return undefined;
    }

    if (status === 'close') {
      const timeout = setTimeout(() => {
        setLockScroll(false);
      }, CLOSE_TRANSITION_DURATION);

      return () => {
        clearTimeout(timeout);
      };
    }

    setLockScroll(false);

    return undefined;
  }, [status]);

  if (!floatingContext.open && !isMounted) return null;

  const PrimaryButton = primaryAction && (
    <Button
      variant="primary"
      preventDoubleClick={true}
      onClick={() => {
        void primaryAction.action();
      }}
      icon={primaryAction.icon}
      iconPlacement="after"
      isLoading={primaryAction.isLoading}
      disabled={primaryAction.disabled}
      data-testid="primary-action-button"
    >
      {primaryAction.label}
    </Button>
  );
  const SecondaryButton = secondaryAction && (
    <Button
      variant="tertiary"
      preventDoubleClick={true}
      onClick={() => {
        void secondaryAction.action();
      }}
      isLoading={secondaryAction.isLoading}
      disabled={secondaryAction.disabled}
      data-testid="secondary-action-button"
    >
      {secondaryAction.label}
    </Button>
  );

  return (
    <FloatingPortal id="floating-ui-portal-root">
      <OverlayWrapper
        data-status={status}
        data-testid="drawer-overlay"
        lockScroll={lockScroll}
        onClick={(event) => {
          // Close the drawer directly when clicking the greyed-out overlay, if enabled.
          // We call `onClose` here (the same callback used by the close button/actions)
          // rather than relying solely on floating-ui's `useDismiss` outside-press handling,
          // since that path can be short-circuited by an externally supplied `onOpenChange`
          // (e.g. Storybook's actions addon auto-mocking `onOpenChange` for any `on*` prop).
          if (event.target === event.currentTarget && (closeOnClickOutside ?? true)) {
            onClose?.();
          }
        }}
      >
        {/* Focus the floating element itself (rather than the default first-tabbable-child,
            e.g. the close button) on open. Focusing a deep child triggers the browser's native
            focus-triggered "scroll into view" behavior, which - since the child is still
            positioned off-screen via `transform: translateX(100%)` at the moment focus is set -
            forces a layout recalculation that cancels and restarts the slide-in CSS transition,
            causing a visible "bounce". Focusing the floating element avoids this (floating-ui
            skips scroll-into-view when the target *is* the floating element) while still
            establishing the modal focus trap. See https://floating-ui.com/docs/floatingfocusmanager#negative-number */}
        <FloatingFocusManager context={floatingContext} initialFocus={refs.floating}>
          <DrawerBody
            ref={ref}
            {...getFloatingProps(props)}
            data-status={status}
            width={width}
            data-testid="drawer"
          >
            {!disableClose && (
              <DrawerHeader>
                <DrawerTitleWrapper>
                  {title && <DrawerTitle>{title}</DrawerTitle>}
                  {subtitle && <DrawerSubtitle>{subtitle}</DrawerSubtitle>}
                </DrawerTitleWrapper>
                <IconButton
                  variant="secondary"
                  onClick={() => {
                    onClose?.();
                  }}
                  testId="close-drawer-button"
                  aria-label="Close drawer"
                >
                  <Close />
                </IconButton>
              </DrawerHeader>
            )}
            <DrawerScrollableContent>
              <DrawerInner>{props.children}</DrawerInner>
            </DrawerScrollableContent>
            {(primaryAction ?? secondaryAction) && (
              <DrawerFooter>
                {SecondaryButton}
                {PrimaryButton}
              </DrawerFooter>
            )}
          </DrawerBody>
        </FloatingFocusManager>
      </OverlayWrapper>
    </FloatingPortal>
  );
}
